import { useId } from 'react';
import { Modal } from '../../check-batch/components/Modal';
import { NOT_AVAILABLE, formatDate, formatDateTime, formatMoney, plural } from '../../check-batch/format';
import type { ContractorBatch } from '../types';
import { checkNumberRange } from '../helpers';

interface Props {
  batch: ContractorBatch;
  /** `saved`: just saved from Review. `existing`: a saved draft was found when the page opened. */
  variant: 'saved' | 'existing';
  busy: boolean;
  onBackToDashboard: () => void;
  onDiscard: () => void;
  onContinue: () => void;
}

function Card({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }) {
  return (
    <div className="wc-draft__card">
      <dt>{label}</dt>
      <dd data-testid={testId}>{children}</dd>
    </div>
  );
}

/** "Payment Batch Saved as Draft": what the saved draft holds, so the admin can continue, discard (confirmed separately) or leave. */
export function ContractorDraftDialog({ batch, variant, busy, onBackToDashboard, onDiscard, onContinue }: Props) {
  const titleId = useId();
  const bodyId = useId();
  const reference = `CPD-${batch.id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
  const included = batch.lines.filter((l) => l.included);
  return (
    <Modal titleId={titleId} describedById={bodyId} onClose={onContinue} busy={busy} closeLabel="Close draft summary">
      <div className="wc-draft" data-layout={variant === 'saved' ? 'step3' : 'step1'}>
        <svg className="wc-draft__icon" viewBox="0 0 96 96" aria-hidden="true" focusable="false">
          <path fill="currentColor" fillRule="evenodd" d="M18 12h46l14 14v52a6 6 0 0 1-6 6H18a6 6 0 0 1-6-6V18a6 6 0 0 1 6-6Zm10 4v18a3 3 0 0 0 3 3h28a3 3 0 0 0 3-3V19l-3-3H28Zm20 28a13 13 0 1 0 0 26 13 13 0 0 0 0-26Z" />
        </svg>
        <h3 id={titleId} className="wc-draft__title">Payment Batch Saved as Draft</h3>
        <p id={bodyId} className="wc-draft__text">
          {variant === 'saved' ? 'Your payment batch has been saved. You can return to submit it at any time before the pay date.' : 'You have a saved payment batch. Continue editing it, or discard it and start over.'}
        </p>
        <p className="wc-draft__ref" data-testid="draft-batch-id">Draft ID: {reference}</p>
        <dl className="wc-draft__grid">
          <Card label="Payment Method">Write Check</Card>
          <Card label="Pay Date">{formatDate(batch.payDate)}</Card>
          <Card label="Contractors to be paid">{plural(batch.totals.contractorsIncluded, 'Contractor', 'Contractors')}</Card>
          <Card label={included.length === 1 ? 'Check Number' : 'Check Numbers'} testId="draft-check-numbers">{checkNumberRange(included.map((l) => l.checkNumber))}</Card>
          <div className="wc-draft__card wc-draft__card--wide wc-draft__card--amount">
            <div>
              <dt>Total Amount</dt>
              <dd><span className="wc-accent" data-testid="draft-total">{formatMoney(batch.totals.checkTotal)}</span></dd>
            </div>
            <span className="wc-draft__pill"><span className="wc-draft__dot" aria-hidden="true" />Draft</span>
          </div>
          <div className="wc-draft__card wc-draft__card--wide wc-draft__card--split">
            <div><dt>Draft By</dt><dd data-testid="draft-by">{batch.lastSavedByEmail ?? NOT_AVAILABLE}</dd></div>
            <div className="wc-draft__right"><dt>Last Saved</dt><dd data-testid="draft-last-saved">{batch.lastSavedAtUtc ? formatDateTime(batch.lastSavedAtUtc) : NOT_AVAILABLE}</dd></div>
          </div>
        </dl>
        <div className="wc-draft__actions">
          <button type="button" className="wc-btn wc-btn--outline wc-btn--lg" onClick={onBackToDashboard} disabled={busy}>Back to Admin Dashboard</button>
          <span>
            <button type="button" className="wc-link wc-link--muted" onClick={onDiscard} disabled={busy}>Discard Draft</button>
            <button type="button" className="wc-btn wc-btn--green wc-btn--lg" onClick={onContinue} disabled={busy} data-autofocus>Continue Editing</button>
          </span>
        </div>
      </div>
    </Modal>
  );
}
